import type { GlCategory } from '@/services/accountService/glCategoryService';

/** 与 initRows / 后端一致的自动选中阈值 */
export const GL_MAPPING_AUTO_SELECT_MIN_SCORE = 0.6;

export type GlCategoryType = NonNullable<GlCategory['type']>;

export interface HeaderMappingHint {
    types: GlCategoryType[];
    codePrefixes: string[];
    excludeCodePrefixes: string[];
}

const ACC_DEP_PREFIX = 'A-FA-AD';

/**
 * 根据 Excel 列标题推断优先 GL 类型与 code 前缀（旧格式 Column Mapping 用）
 */
export function inferHeaderMappingHint(headerTitle: string): HeaderMappingHint | null {
    const t = (headerTitle || '').trim().toLowerCase();
    if (!t) return null;

    const hint: HeaderMappingHint = {
        types: [],
        codePrefixes: [],
        excludeCodePrefixes: [ACC_DEP_PREFIX],
    };

    if (/\brevenue\b|\bsales\b|\bincome\b|\bturnover\b/.test(t)) {
        hint.types.push('REVENUE');
        hint.codePrefixes.push('R-');
        return hint;
    }
    if (/share\s*capital|issued\s*capital/.test(t)) {
        hint.types.push('EQUITY');
        hint.codePrefixes.push('E-EQ-SC', 'E-EQ-');
        return hint;
    }
    if (/accumulated\s*profit|accumulated\s*loss|profit\s*\/\s*\(?loss/.test(t)) {
        hint.types.push('EQUITY');
        hint.codePrefixes.push('E-EQ-AP', 'E-EQ-');
        return hint;
    }
    if (/accrued\s*expense|accured\s*expense/.test(t)) {
        hint.types.push('LIABILITY');
        hint.codePrefixes.push('L-CL-AC', 'L-CL-');
        return hint;
    }
    if (/director/.test(t)) {
        hint.types.push('LIABILITY', 'ASSET');
        hint.codePrefixes.push('L-CL-DIR', 'A-CA-DFD', 'BS-CL-DTD', 'BS-CA-DIR');
        return hint;
    }
    if (/tax\s*recoverable|tax\s*payable|tax\s*refund/.test(t)) {
        hint.types.push('ASSET', 'LIABILITY');
        hint.codePrefixes.push('A-CA-TRV', 'A-CA-TAXR', 'L-CL-TXP', 'BS-CL-TAXP', 'BS-CA-TAXR');
        return hint;
    }
    if (/virtual\s*office/.test(t)) {
        hint.types.push('EXPENSE');
        hint.codePrefixes.push('X-OPEX-VO', 'X-OPEX-');
        return hint;
    }
    if (/bank|cash|hsbc|deposit/.test(t)) {
        hint.types.push('ASSET');
        hint.codePrefixes.push('A-CA-', 'BS-CA-CASH');
        return hint;
    }
    if (/depreciation|acc\s*dep|accumulated\s*dep/.test(t)) {
        hint.excludeCodePrefixes = [];
        hint.codePrefixes.push(ACC_DEP_PREFIX);
        hint.types.push('ASSET', 'EXPENSE');
        return hint;
    }
    if (/account\s*payable|trade\s*payable/.test(t)) {
        hint.types.push('LIABILITY');
        hint.codePrefixes.push('L-CL-AP', 'L-CL-');
        return hint;
    }
    if (/purchase|cost\s*of\s*sale|cos\b/.test(t)) {
        hint.types.push('EXPENSE');
        hint.codePrefixes.push('X-COS-', 'PL-COT-');
        return hint;
    }

    return null;
}

function matchesHint(cat: GlCategory, hint: HeaderMappingHint): boolean {
    const code = cat.code ?? '';
    if (hint.excludeCodePrefixes.some((p) => code.startsWith(p))) {
        return false;
    }
    const typeOk = hint.types.length === 0 || (cat.type != null && hint.types.includes(cat.type as GlCategoryType));
    const prefixOk = hint.codePrefixes.length === 0 || hint.codePrefixes.some((p) => code.startsWith(p));
    if (hint.types.length > 0 && hint.codePrefixes.length > 0) {
        return typeOk && prefixOk;
    }
    if (hint.types.length > 0) return typeOk;
    if (hint.codePrefixes.length > 0) return prefixOk;
    return true;
}

/**
 * 按列标题过滤 GL 下拉选项；无 hint 时仅排除累计折旧类（除非标题本身与折旧相关）
 * @param fsCode 新格式 Row 4：有则只保留该 FS 下的叶子
 */
export function filterGlCategoriesForHeader(
    categories: GlCategory[],
    headerTitle: string,
    fsCode?: string | null,
): GlCategory[] {
    // 有 Row 4 fsCode：只按 FS 筛叶子（含 A-CA-* / A-SA-* 等），不再用标题前缀二次收窄
    const fs = (fsCode || '').trim();
    if (fs) {
        const scoped = categories.filter((c) => (c.fsCode || '').trim() === fs);
        if (scoped.length > 0) {
            return scoped.filter((c) => !c.code?.startsWith(ACC_DEP_PREFIX));
        }
    }

    const pool = categories;
    const hint = inferHeaderMappingHint(headerTitle || '');
    if (!hint) {
        const depHint = inferHeaderMappingHint('depreciation');
        if (depHint) {
            return pool.filter((c) => matchesHint(c, depHint) || !c.code?.startsWith(ACC_DEP_PREFIX));
        }
        return pool.filter((c) => !c.code?.startsWith(ACC_DEP_PREFIX));
    }
    const filtered = pool.filter((c) => matchesHint(c, hint));
    if (filtered.length > 0) return filtered;
    return pool.filter((c) => !c.code?.startsWith(ACC_DEP_PREFIX));
}

/** 将建议项排在列表前面 */
export function sortGlOptionsWithSuggestion(
    options: GlCategory[],
    suggestedGlCode?: string,
): GlCategory[] {
    if (!suggestedGlCode) return options;
    const suggested = options.find((o) => o.code === suggestedGlCode);
    if (!suggested) return options;
    return [suggested, ...options.filter((o) => o.code !== suggestedGlCode)];
}

/** 仅精确匹配（score=1.0）才预填；导入提醒里的未匹配列不自动选 */
export function shouldAutoSelectSuggestion(matchScore?: number): boolean {
    return (matchScore ?? 0) >= 1.0;
}

/** Co.GL aliasEn/aliasZh → code（与后端 buildAliasToGlCodeMap 一致，key 小写） */
export function buildAliasTitleToCodeMap(
    accounts: Array<{ code?: string | null; aliasEn?: string | null; aliasZh?: string | null }>,
): Map<string, string> {
    const map = new Map<string, string>();
    for (const a of accounts) {
        const code = (a.code || '').trim();
        if (!code) continue;
        if (a.aliasEn?.trim()) map.set(a.aliasEn.trim().toLowerCase(), code);
        if (a.aliasZh?.trim()) map.set(a.aliasZh.trim().toLowerCase(), code);
    }
    return map;
}

/**
 * 权益类标题与 alias code 必须一致（对齐后端 isEquityAliasConsistent），
 * 避免旧别名把 Share capital 指到错误科目。
 */
export function isEquityAliasConsistent(headerTitle: string, glCode: string): boolean {
    const norm = headerTitle.trim().toLowerCase().replace(/\s+/g, ' ');
    if (/\bshare\s*capital\b/.test(norm) || norm === 'issued capital') {
        return glCode === 'E-EQ-SC';
    }
    if (norm.includes('accumulated') && (norm.includes('profit') || norm.includes('loss'))) {
        return glCode === 'E-EQ-AP';
    }
    if (norm === 'dividend') {
        return glCode === 'E-EQ-DIV';
    }
    return true;
}

export type AliasPrefillRow = {
    headerTitle: string;
    selectedGlCode: string | null;
    selectedNameEn: string | null;
    suggestedGlCode?: string;
    suggestedNameEn?: string;
    matchScore?: number;
    matchSource?: string;
    fsCode?: string;
};

/**
 * 用公司已有 Co.GL 别名（及标准 nameEn）预填尚未选择的 Column Mapping 行。
 * 上传时 companyId=0 未走后端 Step B 时，进入映射步仍可自动带上历史别名。
 */
export function applyCompanyAliasToMappingRows<T extends AliasPrefillRow>(
    rows: T[],
    aliasMap: Map<string, string>,
    glByCode: Map<string, Pick<GlCategory, 'code' | 'nameEn' | 'fsCode'>>,
): { rows: T[]; applied: number } {
    let applied = 0;
    const nameEnToCode = new Map<string, string>();
    for (const cat of glByCode.values()) {
        if (cat.code && cat.nameEn?.trim()) {
            nameEnToCode.set(cat.nameEn.trim().toLowerCase(), cat.code);
        }
    }

    const next = rows.map((row) => {
        if (row.selectedGlCode) return row;
        const titleKey = (row.headerTitle || '').trim().toLowerCase();
        if (!titleKey) return row;

        let code = aliasMap.get(titleKey) ?? nameEnToCode.get(titleKey);
        if (!code || !isEquityAliasConsistent(row.headerTitle, code)) return row;

        const cat = glByCode.get(code);
        if (!cat) return row;
        if (row.fsCode?.trim() && cat.fsCode?.trim() && cat.fsCode.trim() !== row.fsCode.trim()) {
            return row;
        }

        applied += 1;
        const nameEn = cat.nameEn ?? null;
        // Excel 无 Row4 FS 时，用所选叶子的 FS 补上，便于筛 GL / Needs review
        const fsCode = (row.fsCode || '').trim()
            || (cat.fsCode || '').trim()
            || undefined;
        return {
            ...row,
            fsCode,
            selectedGlCode: code,
            selectedNameEn: nameEn,
            suggestedGlCode: code,
            suggestedNameEn: nameEn ?? undefined,
            matchScore: 1.0,
            matchSource: 'ALIAS',
        };
    });
    return { rows: next, applied };
}

/** 仅精确匹配展示建议 chip；模糊建议不再展示，避免误导手选 */
export function canApplySuggestionChip(matchScore?: number): boolean {
    return (matchScore ?? 0) >= 1.0;
}

export interface MappingConfirmSummary {
    mappedColumns: number;
    totalRows: number;
    rowsWithAmount: number;
    mappings: Array<{ headerTitle: string; glCode: string; nameEn: string }>;
}

export function buildMappingConfirmSummary(
    mappingRows: Array<{ headerTitle: string; selectedGlCode: string | null; selectedNameEn: string | null }>,
    totalRows: number,
    rowsWithAmount: number,
): MappingConfirmSummary {
    return {
        mappedColumns: mappingRows.length,
        totalRows,
        rowsWithAmount,
        mappings: mappingRows
            .filter((r) => r.selectedGlCode)
            .map((r) => ({
                headerTitle: r.headerTitle,
                glCode: r.selectedGlCode!,
                nameEn: r.selectedNameEn ?? r.selectedGlCode!,
            })),
    };
}

/** 统计 GL 预览原始行中有金额的行数 */
export function countGlSheetRowsWithAmount(
    rows: Array<{ columnValues?: Record<string, number | null | undefined> }> | undefined,
): number {
    if (!rows?.length) return 0;
    return rows.filter((r) => {
        const vals = r.columnValues ?? {};
        return Object.values(vals).some((v) => v != null && v !== 0);
    }).length;
}
